import { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, PlayCircle, Loader2, AlertCircle, ArrowUpRight, RefreshCw } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import PageHeader from "@/layout/PageHeader";
import { API, authHeaders } from "@/lib/api";
import { fetchers, qk, prefetchMonthlyBriefDetail } from "@/lib/queryClient";
import { toast } from "sonner";


function StatusPill({ status }) {
  const map = {
    review: { bg: "#EFF6FF", fg: "#1D4ED8", label: "Review" },
    sent:   { bg: "#ECFDF5", fg: "#047857", label: "Sent" },
    draft:  { bg: "#F1F5F9", fg: "#475569", label: "Draft" },
    failed: { bg: "#FEF2F2", fg: "#B91C1C", label: "Failed" },
    empty:  { bg: "#F1F5F9", fg: "#64748B", label: "Empty" },
    archived: { bg: "#F1F5F9", fg: "#94A3B8", label: "Archived" },
  };
  const m = map[status] || map.draft;
  return (
    <span
      className="mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full"
      style={{ background: m.bg, color: m.fg }}
    >
      {m.label}
    </span>
  );
}


export default function MonthlyBriefs() {
  const [generating, setGenerating] = useState(false);

  const { data, isPending, isError, error, refetch, isFetching } = useQuery({
    queryKey: qk.monthlyBriefs(),
    queryFn: fetchers.monthlyBriefs,
    staleTime: 5 * 60_000,
    refetchInterval: 30_000,
  });
  const rows = data?.data ?? [];

  const generateNow = async () => {
    setGenerating(true);
    try {
      const r = await fetch(`${API}/monthly-brief-v2/generate`, {
        method: "POST",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({}),
      });
      const body = await r.json();
      if (!r.ok) throw new Error(body?.detail || `HTTP ${r.status}`);
      if (body.status === "skipped_existing") {
        toast(`Monthly brief already exists for ${body.month_label}. Open it below.`);
      } else {
        toast.success(`Generation started for ${body.month_label}. This runs ~5-10 min in background.`);
        setTimeout(() => refetch(), 5000);
      }
    } catch (e) {
      toast.error(`Generate failed: ${e.message}`);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div data-testid="monthly-briefs-page">
      <PageHeader
        title="Monthly Briefs"
        subtitle="Previous completed calendar month SGT — auto-delivered on the 1st at 04:00 SGT (06:00 Brisbane)."
      />
      <div className="hm-surface p-4 md:p-6" data-testid="monthly-briefs-panel">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="text-xs text-hm-fg-muted leading-relaxed max-w-xl">
            Product 3 · Monthly Headlines Brief. Top 12 headlines per segment,
            condensed and clickable. Fresh Serper pull + union of the month's
            Daily &amp; Weekly Briefs.
          </div>
          <button
            onClick={generateNow}
            disabled={generating}
            data-testid="monthly-briefs-generate-now"
            className="shrink-0 inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-hm-accent text-hm-bg text-xs font-medium hover:bg-hm-accent-dim disabled:opacity-40"
          >
            {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5" />}
            Generate monthly brief now
          </button>
        </div>

        {isError && !data ? (
          <div
            className="p-6 rounded-lg border border-hm-border bg-hm-bg-1 flex items-start gap-4"
            data-testid="monthly-briefs-error"
          >
            <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" style={{ color: "#B45309" }} />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-hm-fg font-medium">
                Unable to load monthly briefs right now
              </div>
              <p className="mono text-[11px] text-hm-fg-muted mt-1">
                {error?.status
                  ? `The server returned ${error.status}. This is usually transient.`
                  : "The connection failed. This is usually transient."}
              </p>
              <button
                onClick={() => refetch()}
                disabled={isFetching}
                data-testid="monthly-briefs-retry"
                className="mt-3 inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium border border-hm-border text-hm-fg hover:border-hm-border-strong disabled:opacity-40"
              >
                {isFetching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                Retry
              </button>
            </div>
          </div>
        ) : isPending && !data ? (
          <div className="rounded-lg border border-hm-border overflow-hidden">
            <div className="grid grid-cols-[1.5fr_0.7fr_0.6fr_0.9fr_auto] gap-4 px-5 py-3 bg-hm-bg-1 mono text-[10px] uppercase tracking-widest text-hm-fg-subtle">
              <div>Month</div>
              <div>Status</div>
              <div className="text-right">Headlines</div>
              <div>Created</div>
              <div></div>
            </div>
            <div className="divide-y divide-hm-border" data-testid="monthly-briefs-skeleton">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="grid grid-cols-[1.5fr_0.7fr_0.6fr_0.9fr_auto] gap-4 px-5 py-4 items-center">
                  <div>
                    <div className="h-3.5 w-40 rounded bg-hm-bg-1 animate-pulse" />
                    <div className="h-2.5 w-28 rounded bg-hm-bg-1 animate-pulse mt-2 opacity-70" />
                  </div>
                  <div className="h-4 w-14 rounded-full bg-hm-bg-1 animate-pulse" />
                  <div className="h-3 w-8 rounded bg-hm-bg-1 animate-pulse ml-auto" />
                  <div className="h-3 w-24 rounded bg-hm-bg-1 animate-pulse" />
                  <div />
                </div>
              ))}
            </div>
          </div>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center" data-testid="monthly-briefs-empty">
            <CalendarClock className="h-6 w-6 text-hm-fg-subtle mx-auto mb-3" />
            <div className="mono text-[11px] uppercase tracking-widest text-hm-fg-subtle">
              No monthly briefs yet.
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-hm-border overflow-hidden">
            <div className="grid grid-cols-[1.5fr_0.7fr_0.6fr_0.9fr_auto] gap-4 px-5 py-3 bg-hm-bg-1 mono text-[10px] uppercase tracking-widest text-hm-fg-subtle">
              <div>Month</div>
              <div>Status</div>
              <div className="text-right">Headlines</div>
              <div>Created</div>
              <div></div>
            </div>
            <div className="divide-y divide-hm-border">
              {rows.map((r) => (
                <Link
                  key={r.id}
                  to={`/monthly-briefs/${r.id}`}
                  onMouseEnter={() => prefetchMonthlyBriefDetail(r.id)}
                  onFocus={() => prefetchMonthlyBriefDetail(r.id)}
                  data-testid={`monthly-briefs-row-${r.id.slice(0,8)}`}
                  className="grid grid-cols-[1.5fr_0.7fr_0.6fr_0.9fr_auto] gap-4 px-5 py-4 items-center hover:bg-hm-bg-1/40 transition-colors"
                >
                  <div>
                    <div className="text-sm text-hm-fg font-medium">{r.month_label}</div>
                    <div className="mono text-[10px] text-hm-fg-subtle mt-0.5">
                      {r.month_start} → {r.month_end}
                    </div>
                  </div>
                  <div><StatusPill status={r.status} /></div>
                  <div className="text-right tabular-nums text-sm text-hm-fg">
                    {r.article_count ?? 0}
                  </div>
                  <div className="mono text-[11px] text-hm-fg-muted">
                    {new Date(r.created_at).toLocaleString(undefined, {
                      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                    })}
                  </div>
                  <div className="text-hm-fg-subtle">
                    <ArrowUpRight className="h-4 w-4" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
